import React, { useRef, useCallback, useState, useContext, useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, svgSprite } from '../../../hybrid/wrappers';
import FormComponent from '../FormComponent/FormComponent';
import FormButtons from '../form-control-buttons/FormButtons/FormButtons';
import UserContext from '../../../common/utils/getContext';
import { sendFunc } from '../utils/groupFromsUtils';
import T, { t } from '../../../common/components/T';
import { LangContext } from '../../../common/decorators/Language';
import { useWidgetTranslates } from '../../../common/utils/utils';
import css from './GroupFormComponent.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../common/utils/styles';

const GroupFormComponent = ({
    data = {},
    groupFormName = 'groupForm',
    sendData,
    formStyles,
    funcs = {},
    customStyles = {},
    buttonText,
    cancelText,
    repeatText,
    customData = {},
    formDataMain,
    setFormDataMain,
    message,
    tabsData,
    formDataError = false,
    buttonNotShow = false,
    token = false,
    active = false,
    deleteHandler,
    dataFields,
    okButtonLoader
}) => {
    const { userToken, setAlertData, lang, localization } = useContext(UserContext);
    const [allowedSend, setAllowedSend] = useState(true);
    const [groupFormDataError, setGroupFormDataError] = useState({});
    const { translation } = useContext(LangContext) || {};
    const [requestLoading, setRequestLoading] = useState(false);
    const initializedData = useMemo(() => {
        if (!(data && Object.keys(data).length)) return { renderData: {}, sendData: {} };
        return Object.keys(data)?.reduce(
            (accum, key) => {
                if (data[key]) {
                    accum.renderData[key] = {
                        ...data[key],
                        fields: data[key]?.fields?.map((field) => {
                            accum.sendData[field?.attribute] = field?.value ?? '';
                            return { ...field, value: field?.value ?? '' };
                        })
                    };
                }
                return accum;
            },
            { renderData: {}, sendData: {} }
        );
    }, [data]);
    const groupFormData = useRef(initializedData.sendData);
    const trans = useWidgetTranslates('groupForm');
    useLayoutEffect(() => {
        groupFormData.current = initializedData.sendData;
        !groupFormDataError && setGroupFormDataError({});
    }, [groupFormDataError, initializedData]);
    const send = useCallback(
        (e, repeat) => {
            if (allowedSend) {
                sendFunc(
                    formDataMain ? { current: formDataMain } : groupFormData,
                    funcs,
                    setAllowedSend,
                    sendData,
                    setGroupFormDataError,
                    setAlertData,
                    userToken,
                    lang,
                    message,
                    repeat,
                    localization?.currency,
                    token,
                    setRequestLoading,
                    okButtonLoader
                );
            }
        },
        [
            localization?.currency,
            allowedSend,
            formDataMain,
            funcs,
            sendData,
            setAlertData,
            userToken,
            lang,
            message,
            token,
            okButtonLoader
        ]
    );
    let buttonsGroup = [];
    if (customData.accordion) {
        if (tabsData?.nextAlias !== 'end') {
            buttonsGroup = [
                {
                    type: 'next',
                    buttonText: cancelText || t('further', 'Далее', 'widget', trans, 'groupForm')
                }
            ];
        } else {
            buttonsGroup.push({
                type: 'ok',
                buttonText: buttonText || t('save', 'Сохранить', 'all', translation)
            });
        }
    } else {
        buttonsGroup = [
            ...(funcs?.cancelFuncs
                ? [
                      {
                          type: 'cancel',
                          buttonText: cancelText || <T textName='back' defaultStr='Назад' page='all' />,
                          clickFunc: funcs.cancelFuncs.web.onClick
                      }
                  ]
                : [])
        ];
        if (funcs?.repeatFunc) {
            buttonsGroup.push({
                type: 'repeat',
                buttonText:
                    repeatText || t('groupForm-SaveAndCreateNew', 'Сохранить и создать новую', 'widget', trans, 'groupForm'),
                clickFunc: (e) => {
                    send(e, funcs.repeatFunc.web.onClick);
                }
            });
        } else if (funcs?.button && active) {
            buttonsGroup.push({
                type: 'button',
                buttonText: repeatText,
                clickFunc: (e) => {
                    funcs.button.web.onClick(e);
                },
                icon: 'plus',
                arrow: true
            });
        }
        buttonsGroup.push({
            type: 'ok',
            buttonText: buttonText || t('save', 'Сохранить', 'all', translation)
        });
    }
    const { stylers, classes } = generateCompStylers(customStyles);
    const { stylersData, classesData } = generateCompStylers(customData, 'Data');

    return (
        <>
            {Object.keys(initializedData?.renderData).map((key, idx) => (
                <React.Fragment key={`singleForm${idx}`}>
                    <div
                        style={active ? stylers.formBlockWrapper : stylersData.customFormBlockWrapper}
                        className={
                            active
                                ? classes.formBlockWrapper
                                : classnames(css.customFormBlockWrapper, classesData.customFormBlockWrapper)
                        }
                    >
                        {dataFields?.length && active ? (
                            <div style={stylers.drugNDrop} className={classes.drugNDrop}>
                                {svgSprite('grid-dots', {
                                    style: {
                                        width: '16px',
                                        height: '16px',
                                        fill: 'var(--greyExtraDarkColor)'
                                    },
                                    onClick: () => {}
                                })}
                            </div>
                        ) : null}
                        <div
                            style={stylers.formBlockContent}
                            className={classnames(css.formBlockContent, classes.formBlockContent)}
                        >
                            {!!initializedData.renderData[key].title ? (
                                <div
                                    key={`id-${Date.now().toString()}${Math.random()}-initializedData.renderData[key].title`}
                                    style={{
                                        ...stylers.groupFormFieldWrapper,
                                        ...stylers.groupFormFieldWrap,
                                        ...(stylers.groupFormSubtitle || stylersData.groupFormSubtitle)
                                    }}
                                    className={classnames(
                                        css.title,
                                        classes.groupFormFieldWrapper,
                                        classes.groupFormFieldWrap,
                                        classes.groupFormSubtitle || classesData.groupFormSubtitle
                                    )}
                                >
                                    <p>{initializedData.renderData[key].title}</p>
                                    {initializedData.renderData[key].info ? (
                                        <div>
                                            <Tooltip
                                                text={initializedData.renderData[key].info}
                                                compStyle={{
                                                    inner: {
                                                        className: classnames(css.formInfoTooltip, classes.formInfoTooltip),
                                                        ...stylers.formInfoTooltip
                                                    }
                                                }}
                                            >
                                                {svgSprite('info-circle', {
                                                    className: classes.infoSpeedProgressTooltipIcon,
                                                    style: stylers.infoSpeedProgressTooltipIcon
                                                })}
                                            </Tooltip>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {!!initializedData.renderData[key].titleElement ? (
                                <div
                                    key={`id-${Date.now().toString()}${Math.random()}-initializedData.renderData[key].titleElement`}
                                    style={{
                                        ...stylers.groupFormFieldWrapper,
                                        ...stylers.groupFormFieldWrap,
                                        ...stylers.groupFormSubtitle
                                    }}
                                    className={classnames(
                                        css.title,
                                        classes.groupFormFieldWrapper,
                                        classes.groupFormFieldWrap,
                                        classes.groupFormSubtitle
                                    )}
                                >
                                    {initializedData.renderData[key].titleElement}
                                </div>
                            ) : null}
                            <FormComponent
                                data={initializedData.renderData[key].fields}
                                captcha={initializedData.renderData[key].captcha}
                                formName={`CreateUserPersonal_data${key}`}
                                add={initializedData.renderData[key].add}
                                sendData={{ sender: send }}
                                setFormDataMain={setFormDataMain || ((key, val) => (groupFormData.current[key] = val || null))}
                                formDataMain={formDataMain || groupFormData.current}
                                groupFormDataError={formDataError || groupFormDataError}
                                formStyles={formStyles}
                                funcs={funcs}
                                message={{
                                    copy:
                                        message?.copy || message?.copy === false
                                            ? message.copy
                                            : t(
                                                  'groupForm-CopyInBuffer',
                                                  'Скопировано в буфер обмена',
                                                  'widget',
                                                  trans,
                                                  'groupForm'
                                              ),
                                    success:
                                        message?.success || message?.success === false
                                            ? message.success
                                            : t(
                                                  'groupForm-succesOperation',
                                                  'Операция прошла успешно',
                                                  'widget',
                                                  trans,
                                                  'groupForm'
                                              ),
                                    error:
                                        message?.error || message?.error === false
                                            ? message.error
                                            : t('groupForm-hapendError', 'Произошла ошибка', 'widget', trans, 'groupForm')
                                }}
                                buttonNotShow
                                customData={{
                                    ...(customStyles?.formStyles
                                        ? {
                                              stylesForm: customStyles.formStyles
                                          }
                                        : {}),
                                    ...customData,
                                    fieldsWrapper: customData?.fieldsWrapper ||
                                        customData[key]?.fieldsWrapper || {
                                            className: classnames(classes.groupFormFieldWrapper, classes.groupFormFieldWrap),
                                            ...stylers.groupFormFieldWrapper,
                                            ...stylers.groupFormFieldWrap
                                        }
                                }}
                            />
                        </div>
                        {dataFields?.length && active ? (
                            <div style={stylers.deleteIcon} className={classes.deleteIcon}>
                                {svgSprite('trash', {
                                    className: classnames(classes.formInputHelpBtnSvg, classes.deleteIconStyles),
                                    style: {
                                        ...stylers.formInputHelpBtnSvg,
                                        ...stylers.deleteIconStyles
                                    },
                                    onClick: () => deleteHandler(initializedData.renderData[key].fields)
                                })}
                            </div>
                        ) : null}
                    </div>
                    {Object.keys(initializedData?.renderData).length !== idx + 1 && (
                        <div style={stylers.hr} className={classnames(css.hr, classes.hr || classesData?.hr)} />
                    )}
                </React.Fragment>
            ))}
            {!buttonNotShow && (
                <div
                    style={{
                        ...stylersData.formButtonBlockWrapper,
                        ...stylers.formBlockWrapper
                    }}
                    className={classesData.formButtonBlockWrapper || classes.formBlockWrapper}
                >
                    <FormButtons
                        formName={groupFormName}
                        buttons={buttonsGroup}
                        sendFormData={send}
                        customFormButton={customData?.customFormButton}
                        tabsData={tabsData}
                        requestLoading={requestLoading}
                    />
                </div>
            )}
        </>
    );
};
GroupFormComponent.propTypes = {
    data: PropTypes.object,
    groupFormName: PropTypes.string,
    sendData: PropTypes.object,
    formStyles: PropTypes.object,
    funcs: PropTypes.object,
    customStyles: PropTypes.object,
    buttonText: PropTypes.node,
    cancelText: PropTypes.node,
    repeatText: PropTypes.node,
    customData: PropTypes.object,
    formDataMain: PropTypes.object,
    setFormDataMain: PropTypes.func,
    message: PropTypes.object,
    tabsData: PropTypes.object,
    formDataError: PropTypes.object,
    buttonNotShow: PropTypes.bool,
    dataType: PropTypes.string,
    token: PropTypes.string,
    active: PropTypes.bool,
    deleteHandler: PropTypes.func,
    dataFields: PropTypes.array
};
export default GroupFormComponent;
